// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md):
// - Live regions must exist on page load (#aria_polite / #aria_assertive in Rails layouts).
// - JAWS reliably announces polite/assertive mutations; VO timing can differ.
// - Clearing then re-setting textContent on a later tick helps both detect repeats.

export const ARIA_POLITE_ID = 'aria_polite'
export const ARIA_ASSERTIVE_ID = 'aria_assertive'

export const SET_DELAY_MS = 60
export const HOLD_MS = 1200

const queue = []
let isProcessing = false
const timeouts = []

const clearTimeouts = () => {
  timeouts.forEach((id) => window.clearTimeout(id))
  timeouts.length = 0
}

const getRegion = (assertive) =>
  document.getElementById(assertive ? ARIA_ASSERTIVE_ID : ARIA_POLITE_ID)

const processQueue = () => {
  if (isProcessing) return
  const next = queue.shift()
  if (!next) return

  const target = getRegion(next.assertive)
  if (!target) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `ariaAnnouncer: #${next.assertive ? ARIA_ASSERTIVE_ID : ARIA_POLITE_ID} not found — is shared/aria_live_regions in the layout?`,
      )
    }
    next.onComplete?.()
    processQueue()
    return
  }

  isProcessing = true
  target.textContent = ''

  const setId = window.setTimeout(() => {
    target.textContent = next.message
    const holdId = window.setTimeout(() => {
      isProcessing = false
      next.onComplete?.()
      processQueue()
    }, HOLD_MS)
    timeouts.push(holdId)
  }, SET_DELAY_MS)
  timeouts.push(setId)
}

/**
 * Announce text via the global aria-live regions in the Rails layout.
 * @param {string} message
 * @param {{ assertive?: boolean, onComplete?: () => void }} [options]
 */
export const announceAria = (
  message,
  { assertive = false, onComplete } = {},
) => {
  const text = typeof message === 'string' ? message.trim() : ''
  if (!text) return
  queue.push({ message: text, assertive, onComplete })
  processQueue()
}

export const focusStepHeading = () => {
  const heading = document.querySelector('[data-onboarding-step-heading]')
  if (heading && typeof heading.focus === 'function') {
    heading.focus()
  }
}

export const resetAriaAnnouncer = () => {
  clearTimeouts()
  queue.length = 0
  isProcessing = false
  const polite = getRegion(false)
  const assertive = getRegion(true)
  if (polite) polite.textContent = ''
  if (assertive) assertive.textContent = ''
}
